// src/services/apiService.js - Updated with direct auth service for user management
import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8090';
const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:8080';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
    });

    // Create separate instance for auth service (user management)
    this.authApi = axios.create({
      baseURL: AUTH_SERVICE_URL,
    });

    this.isRefreshing = false;
    this.failedQueue = [];

    // Request interceptor for main API (gateway)
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Request interceptor for auth API (direct)
    this.authApi.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Enhanced response interceptor for main API
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        return this.handleTokenRefresh(error, this.api);
      }
    );

    // Enhanced response interceptor for auth API
    this.authApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        return this.handleTokenRefresh(error, this.authApi);
      }
    );
  }

  async handleTokenRefresh(error, apiInstance) {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiInstance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      this.isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          console.log('No refresh token available, redirecting to login');
          this.handleLogout();
          return Promise.reject(error);
        }

        console.log('Token expired, attempting refresh...');
        const newTokens = await authService.refreshTokens(refreshToken);
        
        authService.storeTokens(newTokens);
        this.processQueue(null, newTokens.accessToken);
        
        originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
        return apiInstance(originalRequest);
        
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        this.processQueue(refreshError, null);
        this.handleLogout();
        return Promise.reject(refreshError);
      } finally {
        this.isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  handleLogout() {
    console.log('Handling logout due to auth failure');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // ========== AUTHENTICATION & USER API CALLS ==========

  async getUserInfo() {
    try {
      const response = await this.authApi.get('/api/auth/user');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('User Info API endpoint not implemented on backend');
      }
      throw error;
    }
  }

  async getProtectedData() {
    try {
      const response = await this.authApi.get('/api/protected');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Protected Data API endpoint not implemented on backend');
      }
      throw error;
    }
  }

  // ========== USER MANAGEMENT SERVICE API CALLS (Direct to Auth Service) ==========

  async getAllUsers() {
    try {
      const response = await this.authApi.get('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      // Return fallback mock data if API fails
      return [
        {
          id: '1',
          username: 'admin',
          email: 'admin@example.com',
          firstName: 'Admin',
          lastName: 'User',
          status: 'CONFIRMED',
          role: 'ADMIN',
          createdAt: new Date().toISOString(),
          enabled: true,
          emailVerified: true
        },
        {
          id: '2', 
          username: 'testuser',
          email: 'test@example.com',
          firstName: 'Test',
          lastName: 'User',
          status: 'CONFIRMED',
          role: 'USER',
          createdAt: new Date().toISOString(),
          enabled: true,
          emailVerified: true
        }
      ];
    }
  }

  async getUserById(userId) {
    try {
      const response = await this.authApi.get(`/api/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }

  async createUser(userData) {
    try {
      const response = await this.authApi.post('/api/admin/users', {
        username: userData.username,
        email: userData.email,
        temporaryPassword: userData.temporaryPassword,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        sendWelcomeEmail: userData.sendWelcomeEmail
      });
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUserRole(userId, role) {
    try {
      const response = await this.authApi.put(`/api/admin/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  async deleteUser(userId) {
    try {
      const response = await this.authApi.delete(`/api/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async resetUserPassword(userId) {
    try {
      const response = await this.authApi.post(`/api/admin/users/${userId}/reset-password`);
      return response.data;
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  }

  async toggleUserStatus(userId, enabled) {
    try {
      const response = await this.authApi.put(`/api/admin/users/${userId}/status`, { enabled });
      return response.data;
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }
  }

  async confirmUser(userId) {
    try {
      const response = await this.authApi.post(`/api/admin/users/${userId}/confirm`);
      return response.data;
    } catch (error) {
      console.error('Error confirming user:', error);
      throw error;
    }
  }

  async getUserStatistics() {
    try {
      const response = await this.authApi.get('/api/admin/users/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        adminUsers: 0,
        regularUsers: 0
      };
    }
  }

  async searchUsers(query, page = 1, size = 20) {
    try {
      const pageIndex = page - 1;
      const response = await this.authApi.get('/api/admin/users/search', {
        params: {
          query: query,
          page: pageIndex,
          size: size
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  async getUsersByStatus(status, page = 1, size = 20) {
    try {
      const pageIndex = page - 1;
      const response = await this.authApi.get('/api/admin/users/by-status', {
        params: {
          status: status,
          page: pageIndex,
          size: size
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching users by status:', error);
      throw error;
    }
  }

  async getUsersPaginated(page = 1, size = 20, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const pageIndex = page - 1;
      const response = await this.authApi.get('/api/admin/users/paginated', {
        params: {
          page: pageIndex,
          size: size,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });

      const data = response.data;
      
      return {
        content: data.content || [],
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        size: data.size || size,
        number: data.number || 0,
        first: data.first || false,
        last: data.last || false
      };
    } catch (error) {
      console.error('Error fetching paginated users:', error);
      throw error;
    }
  }

  // ========== ENHANCED DASHBOARD ==========

  async getAdminDashboardSummary() {
    try {
      const results = await Promise.allSettled([
        this.getOrderStatistics(),
        this.getAllPayments(),
        this.getAllInventory(),
        this.getUserStatistics()
      ]);

      const orderStats = results[0].status === 'fulfilled' ? results[0].value : {};
      const payments = results[1].status === 'fulfilled' ? results[1].value : [];
      const inventory = results[2].status === 'fulfilled' ? results[2].value : [];
      const userStats = results[3].status === 'fulfilled' ? results[3].value : {};

      const paymentsArray = Array.isArray(payments) ? payments : [];
      const inventoryArray = Array.isArray(inventory) ? inventory : [];

      return {
        totalOrders: orderStats.totalOrders || 0,
        totalPayments: paymentsArray.length,
        totalProducts: inventoryArray.length,
        recentOrders: orderStats.recentOrders || [],
        recentPayments: paymentsArray.slice(0, 5),
        lowStockItems: inventoryArray.filter(item => item.quantity < 10),
        ordersByStatus: {
          pending: orderStats.pending_paymentCount || 0,
          success: orderStats.payment_successCount || 0,
          failed: orderStats.payment_failedCount || 0,
          cancelled: orderStats.cancelledCount || 0,
          shipped: orderStats.shippedCount || 0
        },
        totalUsers: userStats.totalUsers || 0,
        activeUsers: userStats.activeUsers || 0,
        pendingUsers: userStats.pendingUsers || 0,
        adminUsers: userStats.adminUsers || 0,
        regularUsers: userStats.regularUsers || 0
      };
    } catch (error) {
      console.error('Failed to get admin dashboard summary:', error);
      return {
        totalOrders: 0,
        totalPayments: 0,
        totalProducts: 0,
        recentOrders: [],
        recentPayments: [],
        lowStockItems: [],
        ordersByStatus: {
          pending: 0,
          success: 0,
          failed: 0,
          cancelled: 0,
          shipped: 0
        },
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        adminUsers: 0,
        regularUsers: 0
      };
    }
  }

  // ========== PRODUCT SERVICE API CALLS (Using Inventory Service) ==========

  async getAllProducts() {
    try {
      const response = await this.api.get('/api/inventory');
      return this.transformInventoryToProducts(response.data);
    } catch (error) {
      console.error('Error fetching products from inventory:', error);
      throw error;
    }
  }

  async getProductsByCategory(category) {
    try {
      const response = await this.api.get('/api/inventory');
      const products = this.transformInventoryToProducts(response.data);
      return products.filter(product => 
        product.category && product.category.toLowerCase() === category.toLowerCase()
      );
    } catch (error) {
      console.error('Error fetching products by category from inventory:', error);
      throw error;
    }
  }

  async getProductById(id) {
    try {
      const response = await this.api.get(`/api/inventory/${id}`);
      return this.transformInventoryItemToProduct(response.data);
    } catch (error) {
      console.error('Error fetching product from inventory:', error);
      throw error;
    }
  }

  async getProductInsights() {
    try {
      const [inventoryResponse, lowStockResponse] = await Promise.all([
        this.api.get('/api/inventory'),
        this.api.get('/api/inventory/low-stock?threshold=10')
      ]);
      
      return this.generateInsightsFromInventory(
        inventoryResponse.data, 
        lowStockResponse.data
      );
    } catch (error) {
      console.error('Error generating product insights from inventory:', error);
      throw error;
    }
  }

  // ========== DATA TRANSFORMATION HELPERS ==========

  transformInventoryToProducts(inventoryData) {
    if (!Array.isArray(inventoryData)) {
      console.warn('Inventory data is not an array:', inventoryData);
      return [];
    }
    return inventoryData.map(item => this.transformInventoryItemToProduct(item));
  }

  transformInventoryItemToProduct(inventoryItem) {
    return {
      id: inventoryItem.productId || inventoryItem.id,
      name: inventoryItem.productName || inventoryItem.name || `Product ${inventoryItem.productId}`,
      description: inventoryItem.description || 'Product from inventory',
      price: inventoryItem.price || inventoryItem.unitPrice || 0,
      category: inventoryItem.category || 'General',
      sku: inventoryItem.sku,
      quantity: inventoryItem.quantity || 0,
      availableQuantity: inventoryItem.availableQuantity || inventoryItem.quantity || 0,
      inStock: (inventoryItem.quantity || 0) > 0,
      stockStatus: this.getStockStatus(inventoryItem.quantity),
      source: 'inventory'
    };
  }

  getStockStatus(quantity) {
    if (quantity <= 0) return 'Out of Stock';
    if (quantity <= 10) return 'Low Stock';
    return 'In Stock';
  }

  generateInsightsFromInventory(inventoryData, lowStockData) {
    const totalProducts = inventoryData.length;
    const inStockProducts = inventoryData.filter(item => (item.quantity || 0) > 0).length;
    const lowStockProducts = lowStockData.length;
    
    return {
      totalProducts,
      inStockProducts,
      outOfStockProducts: totalProducts - inStockProducts,
      lowStockProducts,
      stockHealthPercentage: Math.round((inStockProducts / totalProducts) * 100),
      categories: this.getCategoriesFromInventory(inventoryData)
    };
  }

  getCategoriesFromInventory(inventoryData) {
    const categoryMap = {};
    inventoryData.forEach(item => {
      const category = item.category || 'Uncategorized';
      if (!categoryMap[category]) {
        categoryMap[category] = 0;
      }
      categoryMap[category]++;
    });
    return categoryMap;
  }

  // ========== ORDER SERVICE API CALLS ==========
  
  async getAllOrders() {
    const response = await this.api.get('/api/orders');
    return response.data;
  }

  async getOrderById(orderId) {
    const response = await this.api.get(`/api/orders/${orderId}`);
    return response.data;
  }

  async createOrder(orderData) {
    const response = await this.api.post('/api/orders', orderData);
    return response.data;
  }

  async updateOrderStatus(orderId, status) {
    const response = await this.api.put(`/api/orders/${orderId}/status`, { status });
    return response.data;
  }

  async cancelOrder(orderId) {
    const response = await this.api.delete(`/api/orders/${orderId}`);
    return response.data;
  }

  async getOrderHistory(page = 0, size = 10) {
    const response = await this.api.get(`/api/orders?page=${page}&size=${size}`);
    return response.data;
  }

  // ========== PAGINATION METHODS FOR ORDERS ==========

  async getOrdersPaginated(page = 1, size = 20, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const pageIndex = page - 1;
      
      const response = await this.api.get('/api/orders/paginated', {
        params: {
          page: pageIndex,
          size: size,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });

      const data = response.data;
      
      return {
        content: data.content || [],
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        size: data.size || size,
        number: data.number || 0,
        first: data.first || false,
        last: data.last || false
      };
    } catch (error) {
      console.error('Error fetching paginated orders:', error);
      throw error;
    }
  }

  async searchOrdersPaginated(query, page = 1, size = 20, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const pageIndex = page - 1;
      
      const response = await this.api.get('/api/orders/search', {
        params: {
          query: query,
          page: pageIndex,
          size: size,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });

      const data = response.data;
      
      return {
        content: data.content || [],
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        size: data.size || size,
        number: data.number || 0,
        first: data.first || false,
        last: data.last || false
      };
    } catch (error) {
      console.error('Error searching orders:', error);
      throw error;
    }
  }

  async getOrdersByStatusPaginated(status, page = 1, size = 20, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const pageIndex = page - 1;
      
      const response = await this.api.get('/api/orders/by-status', {
        params: {
          status: status,
          page: pageIndex,
          size: size,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });

      const data = response.data;
      
      return {
        content: data.content || [],
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        size: data.size || size,
        number: data.number || 0,
        first: data.first || false,
        last: data.last || false
      };
    } catch (error) {
      console.error('Error fetching orders by status:', error);
      throw error;
    }
  }

  async getOrderStatistics() {
    try {
      const response = await this.api.get('/api/orders/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching order statistics:', error);
      throw error;
    }
  }

  async getOrderDetails(id) {
    try {
      const response = await this.api.get(`/api/orders/${id}/details`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  }

  async getAllOrdersPaginated(page = 1, size = 20, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const pageIndex = page - 1;
      
      const response = await this.api.get('/api/orders/admin/paginated', {
        params: {
          page: pageIndex,
          size: size,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });

      const data = response.data;
      
      return {
        content: data.content || [],
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        size: data.size || size,
        number: data.number || 0,
        first: data.first || false,
        last: data.last || false
      };
    } catch (error) {
      console.error('Error fetching all orders for admin:', error);
      throw error;
    }
  }

  // ========== INVENTORY SERVICE API CALLS ==========
  
  async getAllInventory() {
    const response = await this.api.get('/api/inventory');
    return response.data;
  }

  async getInventoryById(productId) {
    const response = await this.api.get(`/api/inventory/${productId}`);
    return response.data;
  }

  async updateInventory(productId, quantity) {
    const response = await this.api.put(`/api/inventory/${productId}`, { quantity });
    return response.data;
  }

  async checkProductAvailability(productId, requestedQuantity) {
    const response = await this.api.get(`/api/inventory/${productId}/availability?quantity=${requestedQuantity}`);
    return response.data;
  }

  async getLowStockItems(threshold = 10) {
    const response = await this.api.get(`/api/inventory/low-stock?threshold=${threshold}`);
    return response.data;
  }

  // ========== PAYMENT SERVICE API CALLS ==========
  
  async getAllPayments() {
    const response = await this.api.get('/api/payments');
    return response.data;
  }

  async getPaymentById(paymentId) {
    const response = await this.api.get(`/api/payments/${paymentId}`);
    return response.data;
  }

  async createPayment(paymentData) {
    const response = await this.api.post('/api/payments', paymentData);
    return response.data;
  }

  async getPaymentByOrderId(orderId) {
    const response = await this.api.get(`/api/payments/order/${orderId}`);
    return response.data;
  }

  async processPayment(paymentId) {
    const response = await this.api.post(`/api/payments/${paymentId}/process`);
    return response.data;
  }

  async refundPayment(paymentId, amount) {
    const response = await this.api.post(`/api/payments/${paymentId}/refund`, { amount });
    return response.data;
  }

  async getPaymentMethods() {
    const response = await this.api.get('/api/payments/methods');
    return response.data;
  }

  // ========== COMBINED OPERATIONS ==========
  
  async completeCheckout(checkoutData) {
    try {
      const order = await this.createOrder({
        productId: checkoutData.productId,
        quantity: checkoutData.quantity,
        price: checkoutData.price
      });

      const payment = await this.createPayment({
        orderId: order.id,
        amount: checkoutData.quantity * checkoutData.price,
        paymentMethod: checkoutData.paymentMethod
      });

      return {
        order,
        payment,
        success: true
      };
    } catch (error) {
      console.error('Checkout failed:', error);
      throw error;
    }
  }

  async getOrderWithPayment(orderId) {
    try {
      const [order, payment] = await Promise.all([
        this.getOrderById(orderId),
        this.getPaymentByOrderId(orderId).catch(() => null)
      ]);

      return {
        ...order,
        payment
      };
    } catch (error) {
      console.error('Failed to get order with payment:', error);
      throw error;
    }
  }

  // ========== DASHBOARD & ANALYTICS ==========

  async getDashboardSummary() {
    try {
      const results = await Promise.allSettled([
        this.getOrderStatistics(),
        this.getAllPayments(),
        this.getAllInventory()
      ]);

      const orderStats = results[0].status === 'fulfilled' ? results[0].value : {};
      const payments = results[1].status === 'fulfilled' ? results[1].value : [];
      const inventory = results[2].status === 'fulfilled' ? results[2].value : [];

      const paymentsArray = Array.isArray(payments) ? payments : [];
      const inventoryArray = Array.isArray(inventory) ? inventory : [];

      return {
        totalOrders: orderStats.totalOrders || 0,
        totalPayments: paymentsArray.length,
        totalProducts: inventoryArray.length,
        recentOrders: orderStats.recentOrders || [],
        recentPayments: paymentsArray.slice(0, 5),
        lowStockItems: inventoryArray.filter(item => item.quantity < 10),
        ordersByStatus: {
          pending: orderStats.pending_paymentCount || 0,
          success: orderStats.payment_successCount || 0,
          failed: orderStats.payment_failedCount || 0,
          cancelled: orderStats.cancelledCount || 0,
          shipped: orderStats.shippedCount || 0
        }
      };
    } catch (error) {
      console.error('Failed to get dashboard summary:', error);
      return {
        totalOrders: 0,
        totalPayments: 0,
        totalProducts: 0,
        recentOrders: [],
        recentPayments: [],
        lowStockItems: [],
        ordersByStatus: {
          pending: 0,
          success: 0,
          failed: 0,
          cancelled: 0,
          shipped: 0
        }
      };
    }
  }

  async getPaginatedDashboardData(page = 1, size = 10) {
    try {
      const results = await Promise.allSettled([
        this.getOrdersPaginated(page, size),
        this.getOrderStatistics(),
        this.getAllPayments(),
        this.getLowStockItems()
      ]);

      const orders = results[0].status === 'fulfilled' ? results[0].value : { content: [], totalElements: 0 };
      const orderStats = results[1].status === 'fulfilled' ? results[1].value : {};
      const payments = results[2].status === 'fulfilled' ? results[2].value : [];
      const lowStockItems = results[3].status === 'fulfilled' ? results[3].value : [];

      return {
        orders: orders,
        statistics: orderStats,
        recentPayments: Array.isArray(payments) ? payments.slice(0, 5) : [],
        lowStockItems: Array.isArray(lowStockItems) ? lowStockItems : [],
        summary: {
          totalOrders: orders.totalElements || 0,
          totalPayments: Array.isArray(payments) ? payments.length : 0,
          lowStockCount: Array.isArray(lowStockItems) ? lowStockItems.length : 0
        }
      };
    } catch (error) {
      console.error('Failed to get paginated dashboard data:', error);
      throw error;
    }
  }

  //  RAZORPAY SERVICE API CALLS
async createPaymentOrder(paymentData) {
  try {
    console.log('Calling gateway at:', `${API_BASE_URL}/api/razorpay/create-order`);
    const response = await this.api.post('/api/razorpay/create-order', paymentData);
    console.log('Gateway response:', response.data);
    return response.data;
  } catch (error) {
    console.error('API Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: error.config
    });
    
    // Re-throw with more context
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Payment service error');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Request setup error');
    }
  }
}

async verifyPayment(verificationData) {
  const response = await this.api.post('/api/razorpay/verify-payment', verificationData);
  return response.data;
}

async handlePaymentFailure(failureData) {
  const response = await this.api.post('/api/razorpay/payment-failed', failureData);
  return response.data;
}

async getAllRazorpayPayments() {
  const response = await this.api.get('/api/razorpay/all');
  return response.data;
}

async getRazorpayPaymentById(paymentId) {
  const response = await this.api.get(`/api/razorpay/${paymentId}`);
  return response.data;
}

async searchRazorpayPayments(term) {
  const response = await this.api.get(`/api/razorpay/search?term=${term}`);
  return response.data;
}

// Add to your existing apiService class - PAYMENT COLLECTION API CALLS
async createPaymentLink(linkData) {
  const response = await this.api.post('/api/razorpay/collection/payment-link', linkData);
  return response.data;
}

async getPaymentLinks() {
  const response = await this.api.get('/api/razorpay/collection/payment-links');
  return response.data;
}

async getReceivedPayments(page = 0, size = 20) {
  const response = await this.api.get(`/api/razorpay/collection/received-payments?page=${page}&size=${size}`);
  return response.data;
}

async cancelPaymentLink(linkId) {
  const response = await this.api.post(`/api/razorpay/collection/payment-link/${linkId}/cancel`);
  return response.data;
}

async getPaymentLink(linkId) {
  const response = await this.api.get(`/api/razorpay/collection/payment-link/${linkId}`);
  return response.data;
}
  // ========== UTILITY METHODS ==========

  isAuthenticated() {
    const token = localStorage.getItem('accessToken');
    return !!token;
  }

  logout() {
    this.handleLogout();
  }

  setAuthToken(token) {
    localStorage.setItem('accessToken', token);
  }

  getAuthToken() {
    return localStorage.getItem('accessToken');
  }

  // ========== DEBUG METHODS ==========

  async debugAuth() {
    try {
      const response = await this.api.get('/api/orders/debug-auth');
      return response.data;
    } catch (error) {
      console.error('Debug auth failed:', error);
      throw error;
    }
  }

  async debugPaymentAuth() {
    try {
      const response = await this.api.get('/api/payments/debug-auth');
      return response.data;
    } catch (error) {
      console.error('Payment debug auth failed:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();